/** Wiki Bundles page — curated collections of entries. */
import { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api } from '../../auth/api';
import { Package, Plus, FileText, ArrowLeft, Edit, Trash2, BookOpen } from 'lucide-react';

export default function WikiBundles() {
    const navigate = useNavigate();
    const [bundles, setBundles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        const load = async () => {
            try {
                const res = await api.get('/wiki/bundles');
                setBundles(res.data.bundles || []);
            } catch (e) {
                setError('Failed to load bundles');
            }
            setLoading(false);
        };
        load();
    }, []);

    if (loading) return <div className="max-w-5xl mx-auto p-6 text-slate-400">Loading bundles...</div>;

    return (
        <div className="max-w-5xl mx-auto p-6">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-white mb-1">Wiki Bundles</h1>
                    <p className="text-slate-400 text-sm">Curated collections of related intelligence entries.</p>
                </div>
                <button
                    onClick={() => navigate('/wiki/bundles/new')}
                    className="flex items-center gap-2 px-3 py-2 rounded bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 text-sm font-medium"
                >
                    <Plus size={16} /> New Bundle
                </button>
            </div>

            {error && <div className="mb-4 p-3 rounded bg-red-500/10 border border-red-500/30 text-red-400 text-sm">{error}</div>}

            {bundles.length === 0 ? (
                <div className="text-slate-400 text-sm">
                    No bundles yet. <Link to="/wiki/bundles/new" className="text-amber-400 hover:underline">Create one</Link>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {bundles.map(b => (
                        <BundleCard key={b.id} bundle={b} />
                    ))}
                </div>
            )}
        </div>
    );
}

function BundleCard({ bundle }) {
    const statusColors = {
        published: 'bg-green-500/20 text-green-400',
        draft: 'bg-slate-700 text-slate-400',
    };

    return (
        <Link
            to={`/wiki/bundles/${bundle.slug}`}
            className="p-4 rounded-lg bg-slate-800/50 border border-slate-700 hover:border-amber-500/30 transition-colors block"
        >
            <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                        <Package size={16} className="text-amber-400 flex-shrink-0" />
                        <h3 className="text-base font-semibold text-white truncate">{bundle.title_en}</h3>
                    </div>
                    {bundle.description_en && (
                        <p className="text-sm text-slate-400 line-clamp-2 mb-2">{bundle.description_en}</p>
                    )}
                    <div className="flex items-center gap-3 text-xs text-slate-400">
                        <span className={`px-1.5 py-0.5 rounded ${statusColors[bundle.status] || ''}`}>
                            {bundle.status}
                        </span>
                        <span>{bundle.entry_count} entries</span>
                        {bundle.creator_name && <span>by {bundle.creator_name}</span>}
                    </div>
                </div>
            </div>
        </Link>
    );
}

/** Bundle detail page */
export function WikiBundleDetail() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [bundle, setBundle] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        const load = async () => {
            try {
                const res = await api.get(`/wiki/bundles/${slug}`);
                setBundle(res.data);
            } catch (e) {
                setError('Failed to load bundle');
            }
            setLoading(false);
        };
        load();
    }, [slug]);

    if (loading) return <div className="max-w-4xl mx-auto p-6 text-slate-400">Loading bundle...</div>;
    if (error) return <div className="max-w-4xl mx-auto p-6 text-red-400">{error}</div>;
    if (!bundle) return null;

    return (
        <div className="max-w-4xl mx-auto p-6">
            <div className="flex items-center gap-3 mb-6">
                <Link to="/wiki/bundles" className="flex items-center gap-2 text-slate-400 hover:text-white text-sm">
                    <ArrowLeft size={16} /> All Bundles
                </Link>
            </div>

            <div className="mb-6">
                <div className="flex items-center gap-2 mb-2">
                    <Package size={20} className="text-amber-400" />
                    <h1 className="text-xl font-bold text-white">{bundle.title_en}</h1>
                </div>
                {bundle.description_en && (
                    <p className="text-slate-300 mb-2">{bundle.description_en}</p>
                )}
                <div className="flex items-center gap-3 text-sm text-slate-400">
                    {bundle.creator_name && <span>Created by {bundle.creator_name}</span>}
                    <span>{bundle.entries?.length || 0} entries</span>
                    <span className={`px-2 py-0.5 rounded text-xs ${bundle.status === 'published' ? 'bg-green-500/20 text-green-400' : 'bg-slate-700 text-slate-400'}`}>
                        {bundle.status}
                    </span>
                </div>
            </div>

            <div className="space-y-3">
                {bundle.entries?.map((entry, i) => (
                    <Link
                        key={entry.id}
                        to={`/wiki/entry/${entry.slug}`}
                        className="flex items-start gap-3 p-3 rounded-lg bg-slate-800/50 border border-slate-700 hover:border-amber-500/30 transition-colors"
                    >
                        <div className="flex-shrink-0 w-7 h-7 rounded-full bg-amber-500/20 text-amber-400 flex items-center justify-center text-sm font-bold">
                            {i + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-white">{entry.title_en || entry.title_vi}</div>
                            <div className="text-xs text-slate-400 mt-0.5">
                                {entry.category?.name && <span className="mr-2">{entry.category.name}</span>}
                                {entry.vendor_name && <span>{entry.vendor_name}</span>}
                            </div>
                            {entry.notes && <div className="text-xs text-slate-500 mt-1">{entry.notes}</div>}
                        </div>
                        <FileText size={14} className="text-slate-500 flex-shrink-0 mt-1" />
                    </Link>
                ))}
            </div>
        </div>
    );
}

/** New/Edit bundle page */
export function WikiBundleEdit() {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEdit = id !== 'new';
    const [formData, setFormData] = useState({
        slug: '',
        title_en: '',
        title_vi: '',
        description_en: '',
        description_vi: '',
        entry_ids: [],
    });
    const [entries, setEntries] = useState([]);
    const [availableEntries, setAvailableEntries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        const load = async () => {
            try {
                // Fetch all published entries for selection
                const [entriesRes] = await Promise.all([
                    api.get('/wiki/entries', { params: { status: 'published', limit: 200 } }),
                ]);
                setAvailableEntries(entriesRes.data.entries || []);

                if (isEdit) {
                    const bundleRes = await api.get(`/wiki/bundles/${id}`);
                    const b = bundleRes.data;
                    setFormData({
                        slug: b.slug,
                        title_en: b.title_en || '',
                        title_vi: b.title_vi || '',
                        description_en: b.description_en || '',
                        description_vi: b.description_vi || '',
                        entry_ids: b.entries?.map(e => e.id) || [],
                    });
                    setEntries(b.entries || []);
                }
            } catch (e) {
                setError('Failed to load bundle data');
            }
            setLoading(false);
        };
        load();
    }, [id, isEdit]);

    const handleSubmit = async () => {
        setSaving(true);
        setError('');
        try {
            if (isEdit) {
                await api.patch(`/wiki/bundles/${id}`, formData);
            } else {
                const res = await api.post('/wiki/bundles', formData);
                navigate(`/wiki/bundles/${formData.slug}`);
                return;
            }
            navigate(`/wiki/bundles/${formData.slug}`);
        } catch (e) {
            setError(e.response?.data?.detail || 'Save failed');
        }
        setSaving(false);
    };

    if (loading) return <div className="max-w-3xl mx-auto p-6 text-slate-400">Loading...</div>;

    return (
        <div className="max-w-3xl mx-auto p-6">
            <div className="flex items-center gap-3 mb-6">
                <Link to="/wiki/bundles" className="flex items-center gap-2 text-slate-400 hover:text-white text-sm">
                    <ArrowLeft size={16} /> All Bundles
                </Link>
                <h1 className="text-lg font-bold text-white">{isEdit ? 'Edit Bundle' : 'New Bundle'}</h1>
            </div>

            {error && <div className="mb-4 p-3 rounded bg-red-500/10 border border-red-500/30 text-red-400 text-sm">{error}</div>}

            <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="text-sm font-medium text-slate-200 block mb-1">Slug</label>
                        <input
                            className="w-full bg-slate-800 border border-slate-600 rounded-lg p-2.5 text-slate-100 text-sm"
                            value={formData.slug}
                            onChange={e => setFormData({ ...formData, slug: e.target.value })}
                            placeholder="e.g. surveillance-starter"
                        />
                    </div>
                    <div>
                        <label className="text-sm font-medium text-slate-200 block mb-1">Title (EN) *</label>
                        <input
                            className="w-full bg-slate-800 border border-slate-600 rounded-lg p-2.5 text-slate-100 text-sm"
                            value={formData.title_en}
                            onChange={e => setFormData({ ...formData, title_en: e.target.value })}
                        />
                    </div>
                    <div>
                        <label className="text-sm font-medium text-slate-200 block mb-1">Tiêu đề (VI)</label>
                        <input
                            className="w-full bg-slate-800 border border-slate-600 rounded-lg p-2.5 text-slate-100 text-sm"
                            value={formData.title_vi}
                            onChange={e => setFormData({ ...formData, title_vi: e.target.value })}
                        />
                    </div>
                </div>

                <div>
                    <label className="text-sm font-medium text-slate-200 block mb-1">Description (EN)</label>
                    <textarea
                        className="w-full bg-slate-800 border border-slate-600 rounded-lg p-2.5 text-slate-100 text-sm h-20 resize-none"
                        value={formData.description_en}
                        onChange={e => setFormData({ ...formData, description_en: e.target.value })}
                    />
                </div>

                <div>
                    <label className="text-sm font-medium text-slate-200 block mb-2">Entries in Bundle</label>
                    <div className="flex flex-wrap gap-2 mb-3">
                        {entries.map((entry, i) => (
                            <span key={entry.id} className="px-2 py-1 rounded bg-slate-700 text-slate-200 text-xs flex items-center gap-1">
                                {entry.title_en || entry.title_vi}
                                <button onClick={() => setFormData({ ...formData, entry_ids: formData.entry_ids.filter(eid => eid !== entry.id) })} className="text-slate-500 hover:text-red-400">×</button>
                            </span>
                        ))}
                    </div>
                    <select
                        className="w-full bg-slate-800 border border-slate-600 rounded-lg p-2.5 text-slate-100 text-sm"
                        value=""
                        onChange={e => {
                            const eid = parseInt(e.target.value);
                            if (eid && !formData.entry_ids.includes(eid)) {
                                const entry = availableEntries.find(en => en.id === eid);
                                if (entry) {
                                    setEntries([...entries, entry]);
                                    setFormData({ ...formData, entry_ids: [...formData.entry_ids, eid] });
                                }
                            }
                        }}
                    >
                        <option value="">+ Add entry...</option>
                        {availableEntries
                            .filter(en => !formData.entry_ids.includes(en.id))
                            .map(en => (
                                <option key={en.id} value={en.id}>{en.title_en || en.title_vi}</option>
                            ))}
                    </select>
                </div>

                <div className="flex gap-3 pt-2">
                    <button onClick={handleSubmit} disabled={saving} className="px-4 py-2 rounded bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 text-sm font-medium disabled:opacity-50">
                        {saving ? 'Saving...' : 'Save Bundle'}
                    </button>
                    {isEdit && (
                        <button
                            onClick={async () => {
                                await api.post(`/wiki/bundles/${id}/publish`);
                                navigate(`/wiki/bundles/${id}`);
                            }}
                            className="px-4 py-2 rounded bg-green-500/20 hover:bg-green-500/30 text-green-400 text-sm font-medium"
                        >
                            Publish
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}